<template>
  <form autocomplete="off" class="form" v-on:submit.prevent="submit(form)">
    <div class="flex form-row">
      <div class="col-6">
        <label for="first_name">First Name <span>*</span></label>
        <input id="first_name" type="text" placeholder="First Name" name="first_name"
               v-model="form.first_name" v-validate="'required|max:100'">
        <span v-if="errors.has('first_name')">{{ errors.first('first_name') }}</span>
      </div>
      <div class="col-6">
        <label for="last_name">Last Name <span>*</span></label>
        <input id="last_name" type="text" placeholder="Last Name" name="last_name" v-model="form.last_name"
               v-validate="'required|max:100'">
        <span v-if="errors.has('last_name')">{{ errors.first('last_name') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <div class="col-6">
        <label for="email">Email Address <span>*</span></label>
        <input id="email" type="text" placeholder="Email Address" name="email"
               v-model="form.email" v-validate="'required|email|max:100'">
        <span v-if="errors.has('email')">{{ errors.first('email') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <div class="col-7">
        <label for="institution_name">School/Institution <span>*</span></label>
        <input id="institution_name" type="text" placeholder="School/Institution Name" name="institution_name"
               v-model="form.institution_name" v-validate="'required|max:100'">
        <span v-if="errors.has('institution_name')">{{ errors.first('institution_name') }}</span>
      </div>
      <div class="col-5">
        <label for="job_title">Job Title <span>*</span></label>
        <input id="job_title" type="text" placeholder="Job Title" name="job_title" v-model="form.job_title"
               v-validate="'required|max:100'">
        <span v-if="errors.has('job_title')">{{ errors.first('job_title') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <div class="col-8">
        <label for="address1">Street Address <span>*</span></label>
        <input id="address1" type="text" placeholder="Address" name="address1" v-model="form.address1"
               v-validate="'required|max:100'">
        <span v-if="errors.has('address1')">{{ errors.first('address1') }}</span>
      </div>
      <div class="col-4">
        <label for="address2">Address 2</label>
        <input id="address2" type="text" placeholder="Bldg/Suite" name="address2" v-validate="'max:100'" v-model="form.address2">
        <span v-if="errors.has('address2')">{{ errors.first('address2') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <div class="col-6">
        <label for="city">City <span>*</span></label>
        <input id="city" type="text" placeholder="City" name="city" v-model="form.city"
               v-validate="'required|max:100'">
        <span v-if="errors.has('city')">{{ errors.first('city') }}</span>
      </div>
      <div class="col-2">
        <label for="state">State <span>*</span></label>
        <div class="inline-block relative w-full">
          <select id="state" type="text" name="state" v-model="form.state" v-validate="'required|length:2'">
            <option disabled></option>
            <option v-for="(state, key, index) of states" :value="state" :key="index">{{ state }}</option>
          </select>
          <div class="pointer-events-none absolute pin-y pin-r flex items-center px-2 text-grey-darker">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
          </div>
        </div>
        <span v-if="errors.has('state')">{{ errors.first('state') }}</span>
      </div>
      <div class="col-4">
        <label for="zip">Zip Code <span>*</span></label>
        <input id="zip" type="text" placeholder="Zip Code" name="zip" v-model="form.zip"
               v-validate="'required|regex:^\\d{5}(?:[-\\s]\\d{4})?$'">
        <span v-if="errors.has('zip')">{{ errors.first('zip') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <div class="col-8">
        <label for="additional_info">Comments / Questions</label>
        <textarea
            rows="6"
            wrap="hard"
            id="additional_info"
            placeholder="Please enter any comments or questions"
            name="additional_info"
            v-model="form.additional_info"
            v-validate="'max:1000'"
        ></textarea>
        <span v-if="errors.has('additional_info')">{{ errors.first('additional_info') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <div class="col-4">
        <vue-recaptcha
          name="recaptcha"
          ref="recaptcha"
          @verify="onCaptchaVerified"
          @expired="onCaptchaExpired"
          size="normal"
          sitekey="6LfPhpEUAAAAAJAGW-4YZYvuBzPg98qeYkKq8QJD"
          v-model="form.recaptcha"
          v-validate="'required|min:1'">
        </vue-recaptcha>
        <span v-if="errors.has('recaptcha')">{{ errors.first('recaptcha') }}</span>
      </div>
    </div>
    <div class="flex form-row">
      <button class="body-8 btn" :disabled="pending || errors.any()">Submit</button>
    </div>
  </form>
</template>

<script>
  import { usStates } from '@/common/utils/us-states';
  import VueRecaptcha from 'vue-recaptcha';

  export default {
    name: 'RequestPricingForm',
    components: {
      'vue-recaptcha': VueRecaptcha
    },
    props: {
      pending: {
        default: false,
        type: Boolean
      }
    },
    data() {
      return {
        form: {
          institution_name: null,
          first_name: null,
          last_name: null,
          job_title: null,
          email: null,
          address1: null,
          address2: null,
          city: null,
          state: null,
          zip: null,
          additional_info: null,
          recaptcha: null,
        },
        states: usStates
      }
    },
    methods: {
      submit() {
        this.$validator.validateAll().then((valid) => {
          if (valid) {
            this.$refs.recaptcha.reset();
            this.$emit('submit', this.form);
          }
        })
      },
      onCaptchaVerified(recaptchaToken) {
        this.form.recaptcha = recaptchaToken;
      },
      onCaptchaExpired: function () {
        this.$refs.recaptcha.reset();
      }
    }
  };
</script>
